
import { Typography, Box } from "@mui/material";
import React, { forwardRef, useEffect, useRef } from "react";
import { InformationBox, WarningBox, LinkBox, WidgetLabel } from "components/core";
import { generalStepPropertyTypeListEnum } from 'assets/constants';
import { interpolate } from "components/core";
import { urlConverter } from "utils";
import Questions from "screens/Questions/Questions";
import DefaultQuestion from "components/Question/DefaultQuestion";

const { header, label, warning, information, link, question, defaultQuestion, multiSelection, freeText } = generalStepPropertyTypeListEnum

// Header component that displays the widget title as an h1 element
const renderHeader = ({ text }) => <Typography variant='h1' fontWeight={500} sx={{ pt: 0.5 }}> {text} </Typography>
// Label component that displays the widget title as a paragraph element
const renderLabel = ({ text }) => <WidgetLabel>{interpolate(text, urlConverter, true)} </WidgetLabel>
// Warning component that displays a warning box with the widget title
const renderWarning = ({ text }) => <Box sx={{ pb: 2, pt: 0.5 }}><WarningBox title={text} /></Box>
// Information component that displays an information box with the widget title
const renderInformation = ({ text }) => <Box sx={{ pb: 2, pt: 0.5 }}><InformationBox title={text} /></Box>

const renderLink = (linkData, propertyId) => <Box sx={{ pb: 2, pt: 0.5 }}><LinkBox propertyId={propertyId} linkData={linkData} /></Box>

const renderQuestions = (properties, value) => <Box sx={{ pb: 2, pt: 0.5 }}><Questions key={value.questionKey} value={value} properties={properties} /></Box>

const renderDefaultQuestion = (type, value, index) => <Box sx={{ pb: 2, pt: 0.5 }}><DefaultQuestion key={index} type={type} value={value} /></Box>

function renderWidget({ propertyId, type, value, properties, index }) {
  switch (type) {
    case header: return renderHeader(value)
    case label: return renderLabel(value)
    case warning: return renderWarning(value)
    case information: return renderInformation(value)
    case link: return renderLink(value, propertyId)
    case question:
    case multiSelection:
    case freeText: {
      return renderQuestions(properties, value)
    }
    case defaultQuestion: return renderDefaultQuestion(type, value, index)

    default: return
  }
}

// StepCustomWidget component that renders a single custom widget based on the provided widget data
export const StepCustomWidget = forwardRef(({ propertyId, type, value, index, properties, userData, step, activeStep }, ref) =>
  <Box ref={ref} tabIndex={0} key={`widget_${index}`}> {renderWidget({ propertyId, type, value, properties, index, userData, step, activeStep })} </Box>)

// StepCustomWidgets component that renders custom widgets based on the provided widgets data
export const StepCustomWidgets = ({ widgets }) => (widgets || [])?.map(({ propertyId, type, value, visible }, index, properties) => <StepCustomWidget key={index} {...{ propertyId, type, value, index, properties }} />)

// PropertiesWithWidgets component that renders properties with custom widgets if available, otherwise renders regular properties
export const PropertiesWithWidgets = ({ properties, stepProperties, userData, step, activeStep }) => {
  const containerRef = useRef(null);
  const ref = useRef(null);
  const currentQuestions = (!!activeStep && userData[`single_selection_questions_${activeStep}`]) || {};
  const questionKeys = Object.keys(currentQuestions);


  const prevQuestionKeysLength = useRef(questionKeys.length);
  useEffect(() => {
    if (questionKeys.length > prevQuestionKeysLength.current) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 20);

      prevQuestionKeysLength.current = questionKeys.length;
    }
  }, [questionKeys.length]);


  return (
    <div ref={containerRef}>
      {properties.map(({ type, value, visible, propertyId }, index) => {

        return (
          <React.Fragment key={`propertie_${index}`}>
            {!!generalStepPropertyTypeListEnum[type] && visible ? (
              <StepCustomWidget
                {...{ propertyId, type, value, index, properties, userData, step, activeStep }}
              />
            ) : (
              stepProperties[type]
            )}
            {index === properties.length - 1 && <div ref={ref}></div>}
          </React.Fragment>
        );
      })}
    </div>
  );
};

